import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuid } from "uuid";
import { Auth } from "aws-amplify";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import classNames from "classnames";
import { isEmpty } from "lodash";
import { Helmet } from "react-helmet-async";
import { loginBg } from "../../assets/images";
import {
  logoIcon,
  githubIcon,
  infoIcon,
  showPasswordIcon,
  hidePasswordIcon,
} from "../../assets/icons";
import {
  EMPTY_STRING,
  CACHE_KEYS,
  ROUTES,
  VALIDATION_MESSAGES,
  AWS_USER_EXCEPTION,
  LOGIN_ERROR_MESSAGE,
} from "../../constants";
import { actions } from "../UserProfile/userProfile.reducers";
import {
  fetchUserProfileById,
  updateUserProfile,
  setIsAuthenticated,
  selectIsAuthenticated,
} from "../UserProfile";
import { Alert } from "../../components/molecules";
import { selectAlert } from "../../components/organisms/Alert";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import { actions as spinnerActions } from "../../components/organisms/Spinner/spinner.reducers";
import { selectShouldShowNav } from "./index";
import { captureMessage } from "@sentry/react";

const { X_REQUEST_ID } = CACHE_KEYS;
const { SIGNUP, LOGIN, FORGOT_PASSWORD, SELECT_WORKSPACE, AUTH_CONFIRMATION } =
  ROUTES;
const { SIGNIN } = SPINNER_NAMES;
const { addSpinner, removeSpinner } = spinnerActions;
const { REACT_APP_GITHUB_CLIENT_ID } = process.env;
const { setShouldFetchUser } = actions;
const { INVALID_EMAIL, EMAIL_REQUIRED, MAX_CHAR_LENGTH, PASSWORD_REQUIRED } =
  VALIDATION_MESSAGES;

const LogIn = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const selectedAlert = useSelector(selectAlert);
  const shouldShowNav = useSelector(selectShouldShowNav);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [alertData, setAlertData] = useState(selectedAlert);
  const [isUserConfirmed, setIsUserConfirmed] = useState(false);
  const [isSpinnerActive, setIsSpinnerActive] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const validationSchema = Yup.object({
    userName: Yup.string().email(INVALID_EMAIL).required(EMAIL_REQUIRED),
    password: Yup.string().max(20, MAX_CHAR_LENGTH).required(PASSWORD_REQUIRED),
  });
  useEffect(() => {
    setAlertData(selectedAlert);
  }, [selectedAlert]);

  useEffect(() => {
    if (alertData) {
      dispatch(removeSpinner(SIGNIN));
      setIsSpinnerActive(false);
    }
  }, [alertData, dispatch]);

  const handleOnSubmit = useCallback(
    (values) => {
      dispatch(addSpinner(SIGNIN));
      setIsSpinnerActive(true);
      Auth.signIn(values.userName, values.password)
        .then((res) => {
          dispatch(fetchUserProfileById(res.attributes.sub)).then((res) => {
            if (!isEmpty(res.payload)) {
              dispatch(
                updateUserProfile({
                  id: res.payload.id,
                  user: {
                    lastSignIn: new Date(),
                  },
                })
              ).then(() => {
                dispatch(setShouldFetchUser(false));
                dispatch(setIsAuthenticated(true));
                sessionStorage.setItem(X_REQUEST_ID, uuid());
                dispatch(removeSpinner(SIGNIN));
                setIsSpinnerActive(false);
              });
            } else setAlertData({ message: LOGIN_ERROR_MESSAGE });
          });
        })
        .catch((err) => {
          if (err.name === AWS_USER_EXCEPTION) setIsUserConfirmed(true);
          setAlertData({ message: err.message });
          const { pathname } = location;
          const detailedErrorInfo = {
            url: pathname,
            updatedBody: values,
          };
          captureMessage(
            `${err.message}\n${JSON.stringify(detailedErrorInfo)}`
          );
        });
    },
    [dispatch, location]
  );

  useEffect(() => {
    if (isAuthenticated) navigate(SELECT_WORKSPACE, { replace: true });
  }, [navigate, isAuthenticated]);

  return (
    <>
      <Helmet>
        <title>Login | TavaJS</title>
      </Helmet>
      {!shouldShowNav && (
        <div className="auth-container min-h-screen relative flex items-center">
          <img
            src={loginBg}
            className="w-full h-full absolute top-0 left-0 object-cover pointer-events-none"
            alt="login-bg"
          />
          <div className="auth-box text-gray-50 relative w-full mx-auto py-12 px-6 sm:w-1/2 md:w-1/2 lg:w-1/3 2xl:w-1/4">
            <div className="auth-header mb-8">
              {alertData && (
                <div className="my-2">
                  <Alert
                    type={alertData.type}
                    message={alertData.message}
                    handleOnClose={setAlertData}
                  />
                </div>
              )}
              <div className="logo mb-6">
                <img src={logoIcon} width={52} alt="logo-icon" />
              </div>
              <h1 className="text-3xl leading-9 font-extrabold mb-2">
                Welcome Back
              </h1>
              <span className="text-6">
                Or&nbsp;
                <Link
                  to={LOGIN}
                  className="text-indigo-200 hover:text-indigo-500"
                >
                  start your 14-day free trial
                </Link>
              </span>
            </div>
            <div className="auth-body">
              <Formik
                initialValues={{
                  userName: EMPTY_STRING,
                  password: EMPTY_STRING,
                }}
                validationSchema={validationSchema}
                onSubmit={handleOnSubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                }) => (
                  <Form>
                    <div className="form-group">
                      <label className="text-6 mb-1 block">Sign in with</label>
                      <Link
                        to={`https://github.com/login/oauth/authorize?client_id=${REACT_APP_GITHUB_CLIENT_ID}&scope=user:email`}
                        className="btn btn-light block w-full text-center"
                      >
                        <img
                          src={githubIcon}
                          width={20}
                          className="mx-auto"
                          alt="github-icon"
                        />
                      </Link>

                      <div className="separator flex items-center my-6">
                        <div className="flex-1 border-t border-white" />
                        <span className="text-sm leading-5 font-normal px-2">
                          Or continue with
                        </span>
                        <div className="flex-1 border-t border-white" />
                      </div>
                      <div className="form-group mb-6">
                        <label className="text-sm font-medium mb-1 block">
                          Email
                        </label>
                        <Field
                          name="userName"
                          className={classNames("form-control border-2", {
                            "border-red-600":
                              touched.userName && errors.userName,
                          })}
                          type="text"
                          placeholder="Your Email Address"
                          value={values.userName}
                          onBlur={(e) => {
                            handleBlur(e);
                            setIsUserConfirmed(false);
                          }}
                          onChange={handleChange}
                        />
                        {touched.userName && errors.userName && (
                          <div className="font-medium text-red-500 text-sm absolute">
                            {errors.userName}
                          </div>
                        )}
                        {isUserConfirmed && (
                          <Link
                            to={`${AUTH_CONFIRMATION}?email=${encodeURIComponent(
                              values.userName
                            )}`}
                            className="text-6 ml-auto text-indigo-200 flex items-center hover:text-indigo-500 mt-3"
                          >
                            <img
                              src={infoIcon}
                              alt="info-icon"
                              className="w-5 h-5 mr-1"
                            />
                            Click here to confirm your Email
                          </Link>
                        )}
                      </div>
                      <div className="form-group mb-6 ">
                        <label className="text-sm font-medium mb-1 block">
                          Password
                        </label>
                        <div
                          className={classNames(
                            "flex items-center bg-[#E8F0FE] focus-within:border-indigo-500 border-2 rounded-md",
                            {
                              "border-red-600":
                                touched.password && errors.password,
                            }
                          )}
                        >
                          <Field
                            name="password"
                            className="form-control border-0 rounded-none !ring-0"
                            type={showPassword ? "text" : "password"}
                            placeholder="Your Password"
                            value={values.password}
                            onBlur={handleBlur}
                            onChange={handleChange}
                          />
                          <img
                            className="w-8 h-4 cursor-pointer"
                            src={
                              showPassword ? showPasswordIcon : hidePasswordIcon
                            }
                            alt="Password"
                            onClick={() => setShowPassword(!showPassword)}
                          />
                        </div>
                        {touched.password && errors.password && (
                          <div className="font-medium text-red-500 text-sm absolute">
                            {errors.password}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center mb-6">
                        <div>
                          <label className="flex items-center">
                            <Field
                              type="checkbox"
                              name="rememberMe"
                              className="rounded h-4 text-indigo-600"
                            />
                            <span className="text-6 ml-2">Remember me</span>
                          </label>
                        </div>
                        <Link
                          to={FORGOT_PASSWORD}
                          onClick={() => {
                            dispatch(removeSpinner(SIGNIN));
                            setIsSpinnerActive(false);
                          }}
                          className="text-6 ml-auto text-indigo-200 hover:text-indigo-500"
                        >
                          Forgot your password?
                        </Link>
                      </div>
                      <button
                        type="submit"
                        className="btn btn-primary block w-full text-center mb-2"
                        disabled={isSpinnerActive}
                      >
                        <Spinner
                          name={SIGNIN}
                          setIsSpinnerActive={setIsSpinnerActive}
                          persistSize
                        >
                          Sign in
                        </Spinner>
                      </button>
                      <div className="w-full text-center text-sm text-indigo-220 hover:text-indigo-500">
                        <Link
                          to={SIGNUP}
                          onClick={() => {
                            dispatch(removeSpinner(SIGNIN));
                            setIsSpinnerActive(false);
                          }}
                          className="font-bold"
                        >
                          Redirect to Sign Up page
                        </Link>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LogIn;
